import { useEffect, useRef } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "./tokens";
import { sectionColorHex } from "./sectionColor";
import CircularProgress from "./CircularProgress";
import type { SectionDef } from "./types";

const useStyles = makeStyles({
  root: {
    height: "100%",
    width: "100%",
    // Füllt den Container auch dann, wenn dieser ein Flex-Container ist und
    // `height: 100%` sich mangels definierter Elternhöhe zu `auto` auflöst.
    // Ausserhalb eines Flex-Containers ist `flex-grow` wirkungslos — die Regel
    // kostet dort also nichts.
    flexGrow: 1,
    minHeight: 0,
    overflowY: "auto",
    backgroundColor: BRAND.ink,
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
  },
  header: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    backgroundColor: BRAND.ink,
    paddingLeft: "20px",
    paddingRight: "20px",
    paddingTop: "20px",
    paddingBottom: "12px",
    ...shorthands.borderBottom("1px", "solid", "rgba(255,255,255,0.06)"),
  },
  headerText: {
    flexGrow: 1,
    minWidth: 0,
  },
  headerEyebrow: {
    fontFamily: BRAND.fontAccent,
    fontSize: "0.7rem",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: BRAND.gold,
  },
  headerTitle: {
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.7)",
  },
  progressBlock: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    flexShrink: 0,
  },
  progressMeta: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    rowGap: "2px",
    lineHeight: 1,
  },
  progressTitle: {
    fontSize: "0.6rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.55)",
  },
  progressDetail: {
    fontSize: "0.7rem",
    color: "rgba(255,255,255,0.75)",
  },
  /** "Aktueller Punkt" pinned at the top of the scrollable area. */
  pinned: {
    position: "sticky",
    // Klebt unter der Kopfzeile. Deren Hoehe schwankt (Fortschrittsring ja/nein,
    // Zeilenumbruch im Titel) und wird deshalb gemessen und als CSS-Variable
    // gesetzt — 60px waren geraten und um ~17px zu klein.
    top: "var(--toc-kopf-hoehe, 60px)",
    zIndex: 1,
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingTop: "10px",
    paddingBottom: "10px",
    backgroundColor: "rgba(0,0,0,0.35)",
    backdropFilter: "blur(2px)",
    ...shorthands.borderBottom("1px", "solid", "rgba(255,255,255,0.06)"),
  },
  /** Mit Umschalter ist dessen Kante schon belegt — der Streifen scrollt mit. */
  pinnedMitScope: {
    position: "static",
  },
  pinnedLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.66rem",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.55)",
    marginBottom: "4px",
  },
  pinnedTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    fontWeight: 400,
    color: BRAND.white,
    lineHeight: 1.2,
  },
  /** Neutral: der Abschnittsname vor dem Schritt trägt die EINE Akzentfarbe. */
  pinnedAbschnittNeutral: {
    color: BRAND.gold,
  },
  scope: {
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
    // Klebt direkt unter der Kopfzeile (gemessen, siehe `pinned`). Der
    // Umschalter ist die Ebene, die aus dem Verzeichnis herausgenommen wurde —
    // er darf beim Scrollen der langen Liste nicht verschwinden.
    position: "sticky",
    top: "var(--toc-kopf-hoehe, 60px)",
    zIndex: 2,
    backgroundColor: BRAND.ink,
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingTop: "10px",
    paddingBottom: "10px",
    ...shorthands.borderBottom("1px", "solid", "rgba(255,255,255,0.06)"),
  },
  scopeLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.66rem",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.55)",
  },
  scopePills: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "6px",
    rowGap: "6px",
  },
  scopePill: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "rgba(255,255,255,0.08)",
    backgroundImage: "none",
    ...shorthands.border("1px", "solid", "transparent"),
    ...shorthands.borderRadius("999px"),
    color: "rgba(255,255,255,0.8)",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.76rem",
    paddingLeft: "10px",
    paddingRight: "10px",
    paddingTop: "4px",
    paddingBottom: "4px",
    maxWidth: "100%",
    whiteSpace: "nowrap",
    overflowX: "hidden",
    textOverflow: "ellipsis",
    transitionProperty: "background-color, color",
    transitionDuration: "120ms",
    ":hover": {
      color: BRAND.white,
      backgroundColor: "rgba(255,255,255,0.16)",
    },
  },
  scopePillActive: {
    backgroundColor: BRAND.gold,
    color: BRAND.ink,
    fontWeight: 700,
  },
  list: {
    listStyleType: "none",
    margin: 0,
    paddingLeft: "8px",
    paddingRight: "8px",
    paddingBottom: "32px",
  },
  sectionItem: {
    marginBottom: "16px",
  },
  sectionRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    paddingTop: "8px",
    paddingBottom: "8px",
    ...shorthands.borderRadius("6px"),
  },
  /**
   * Anklickbare Abschnittszeile. Abschnitte der neuen Gliederung (E14) haben
   * einen eigenen Schritt als Kopf — „Dach", „Anbauten", „Gutachten" bestehen
   * sogar nur daraus. Ohne klickbare Zeile wären sie unerreichbar.
   */
  sectionRowBtn: {
    width: "100%",
    boxSizing: "border-box",
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "transparent",
    backgroundImage: "none",
    ...shorthands.border("0"),
    textAlign: "left",
    fontFamily: BRAND.fontPrimary,
    transitionProperty: "background-color",
    transitionDuration: "120ms",
    ":hover": {
      backgroundColor: "rgba(255,255,255,0.08)",
    },
  },
  sectionRowActive: {
    color: BRAND.white,
  },
  sectionRowInactive: {
    color: "rgba(255,255,255,0.8)",
  },
  /**
   * Neutrale Auswahl (Auftraggeber, 18.08.2026): statt der kräftigen
   * Abschnittsfarbe als Fläche ein aufgehellter Grund + Fettung + eine
   * 3 px-Kante in der Markenfarbe. Die Kante ist ein `inset`-Schatten, kein
   * `border` — sonst verschöbe sich die Zeile beim Wechsel um 3 px.
   *
   * Erkennbar auch ohne Farbwahrnehmung: Helligkeit (Grund), Gewicht (fett)
   * und Form (Kante) tragen die Markierung, nicht der Farbton. Weiss auf dem
   * aufgehellten Grund (#463044) hat rund 11,9:1 Kontrast.
   */
  sectionRowActiveNeutral: {
    backgroundColor: "rgba(255,255,255,0.12)",
    color: BRAND.white,
    fontWeight: 700,
    boxShadow: `inset 3px 0 0 0 ${BRAND.gold}`,
  },
  sectionNumber: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    flexShrink: 0,
    ...shorthands.borderRadius("999px"),
    fontSize: "0.78rem",
    fontWeight: 700,
  },
  sectionNumberInactive: {
    backgroundColor: "rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.85)",
  },
  sectionNumberActive: {
    backgroundColor: "rgba(255,255,255,0.3)",
    color: BRAND.white,
  },
  /**
   * Neutral: die Nummer des aktiven Abschnitts kehrt um (dunkel auf weiss,
   * ~16,9:1) statt eine eigene Farbe zu tragen. Die inaktiven Nummern bleiben
   * beim gedeckten Grau der Basisklasse — damit rotiert nichts mehr.
   */
  sectionNumberActiveNeutral: {
    backgroundColor: BRAND.white,
    color: BRAND.ink,
  },
  sectionTitle: {
    fontSize: "0.9rem",
    fontWeight: 600,
  },
  subList: {
    listStyleType: "none",
    margin: 0,
    paddingLeft: "34px",
    paddingTop: "4px",
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
  },
  subBtn: {
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "transparent",
    backgroundImage: "none",
    ...shorthands.border("0"),
    color: "rgba(255,255,255,0.6)",
    textAlign: "left",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.8rem",
    lineHeight: 1.35,
    paddingLeft: "12px",
    paddingRight: "12px",
    paddingTop: "6px",
    paddingBottom: "6px",
    ...shorthands.borderRadius("4px"),
    transitionProperty: "color, background-color",
    transitionDuration: "120ms",
    ":hover": {
      color: BRAND.white,
    },
  },
  subBtnActive: {
    backgroundColor: "rgba(255,255,255,0.1)",
    fontWeight: 700,
  },
  /** Neutral: reines Weiss statt der Abschnittsfarbe (~15:1 auf dem Grund). */
  subBtnActiveNeutral: {
    color: BRAND.white,
  },
  subBtnPast: {
    color: "rgba(255,255,255,0.65)",
  },
});

/**
 * Auswahl oberhalb des Verzeichnisses — z. B. das Gebäude, dessen Abschnitte
 * gezeigt werden. `SectionDef` kennt nur zwei Ebenen (Abschnitt → Schritt);
 * die neue Immobilien-Gliederung (E14) belegt beide, also wird die dritte
 * Ebene hier zu einem Umschalter statt zu einer weiteren Verschachtelung.
 */
export type SidebarScopeSwitcher = {
  label: string;
  items: { id: string; label: string }[];
  activeId: string;
  onChange: (id: string) => void;
};

type Props = {
  sections: SectionDef[];
  /** -1 = der aktuelle Schritt liegt außerhalb der gezeigten Abschnitte. */
  currentSectionIdx: number;
  /** -1 = der Abschnittskopf selbst ist der aktuelle Schritt. */
  currentSubStepIdx: number;
  onSelect: (sectionIdx: number, subStepIdx: number) => void;
  /**
   * Wenn gesetzt, ist die Abschnittszeile selbst anklickbar und führt zum
   * Kopf-Schritt des Abschnitts. Ohne diesen Callback bleiben Abschnitte
   * ohne Untereinträge unerreichbar.
   */
  onSectionSelect?: (sectionIdx: number) => void;
  progressFraction?: number;
  progressDetail?: string;
  /** Kopfzeile — Default: „Risikoanalyse" / „Übersicht". */
  eyebrow?: string;
  heading?: string;
  ariaLabel?: string;
  /**
   * Deckelt die Höhe des `<nav>` — nötig, sobald das umgebende `<aside>` auf
   * Auto-Höhe steht (z. B. sticky): ohne Deckel wüchse die Liste beliebig und
   * würde nicht mehr in sich scrollen.
   *
   * Die Höhe selbst wird davon NICHT angetastet: das `<nav>` bleibt auf
   * `height: 100%` / `flex-grow: 1` und füllt seinen Container. Ein früher hier
   * gesetztes `height: "auto"` war genau der Grund, warum die dunkle Leiste vor
   * dem unteren Rand endete und darunter ein heller Streifen stehen blieb.
   */
  maxHeight?: number | string;
  scopeSwitcher?: SidebarScopeSwitcher;
  /**
   * `"abschnitte"` (Default) = jede Abschnittsfarbe wird als Akzent gezeigt —
   * so kennen es Risikoanalyse-Kurzform, KFZ und Kennzahlen.
   * `"neutral"` = EINE zurückhaltende Auswahl-Optik ohne Farbreigen
   * (Immobilien-Verzeichnis, Auftraggeber 18.08.2026).
   */
  farbschema?: "abschnitte" | "neutral";
  /**
   * Zusätzlicher Freiraum unter dem letzten Eintrag, in px. Gebraucht, wenn
   * eine Leiste über dem unteren Rand des Verzeichnisses liegt (Vollseite:
   * die Schritt-Leiste läuft dort über die volle Breite und deckt den Fuss der
   * Liste ab).
   */
  bottomInset?: number;
};

const Sidebar = ({
  sections,
  currentSectionIdx,
  currentSubStepIdx,
  onSelect,
  onSectionSelect,
  progressFraction,
  progressDetail,
  eyebrow = "Risikoanalyse",
  heading = "Übersicht",
  ariaLabel = "Abschnitte der Risikoanalyse",
  maxHeight,
  scopeSwitcher,
  farbschema = "abschnitte",
  bottomInset = 0,
}: Props) => {
  const styles = useStyles();
  const neutral = farbschema === "neutral";
  const rootRef = useRef<HTMLDivElement | null>(null);
  const activeBtnRef = useRef<HTMLButtonElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const scopeRef = useRef<HTMLDivElement | null>(null);
  const pinnedRef = useRef<HTMLDivElement | null>(null);

  /**
   * Die klebenden Blöcke (`pinned`, `scope`) müssen exakt unter der Kopfzeile
   * sitzen. Deren Höhe schwankt — Fortschrittsring ja/nein, Zeilenumbruch im
   * Titel, Schriftgrößen des Nutzers —, deshalb wird sie gemessen und als
   * CSS-Variable gesetzt, statt sie im Stylesheet zu raten.
   */
  useEffect(() => {
    const kopf = headerRef.current;
    const wurzel = rootRef.current;
    if (!kopf || !wurzel) return;
    const setzen = () => {
      wurzel.style.setProperty("--toc-kopf-hoehe", `${kopf.offsetHeight}px`);
    };
    setzen();
    if (typeof ResizeObserver === "undefined") return;
    const beobachter = new ResizeObserver(setzen);
    beobachter.observe(kopf);
    return () => beobachter.disconnect();
  });

  const hatScope = Boolean(scopeSwitcher && scopeSwitcher.items.length > 1);
  const currentSection = sections[currentSectionIdx];
  const currentSubStep = currentSection?.subSteps[currentSubStepIdx];

  /**
   * Schlüssel des markierten Eintrags. Als Effekt-Abhängigkeit belastbarer als
   * die beiden Indizes: beim Wechsel des Gebäudes wird die Liste neu gebaut,
   * die Indizes können dabei gleich bleiben (z. B. 1/-1) — der aktive Eintrag
   * ist aber ein anderer und muss sichtbar werden.
   */
  const aktiverKey =
    currentSubStepIdx >= 0 ? currentSubStep?.key : currentSection?.key;

  // Der markierte Eintrag soll sichtbar sein — mehr nicht. Früher wurde er bei
  // JEDEM Schrittwechsel auf eine feste Höhe gezogen; die Liste rutschte dann
  // auch dann, wenn der Eintrag längst frei stand.
  useEffect(() => {
    const root = rootRef.current;
    const btn = activeBtnRef.current;
    if (!root || !btn) return;
    // Oben klebt immer die Kopfzeile, darunter je nach Fall der
    // Gebäude-Umschalter (sticky) ODER der Streifen „Aktueller Punkt" — mit
    // Umschalter scrollt dieser mit und verdeckt nichts.
    const verdeckt =
      (headerRef.current?.offsetHeight ?? 0) +
      (scopeRef.current?.offsetHeight ?? pinnedRef.current?.offsetHeight ?? 0);
    const rootRect = root.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    const obenSichtbar = rootRect.top + verdeckt;
    const untenSichtbar = rootRect.bottom - 8;
    if (btnRect.top >= obenSichtbar && btnRect.bottom <= untenSichtbar) return;
    const delta = btnRect.top - (obenSichtbar + 8);
    // `scrollTo` auf einem Element gibt es in jsdom nicht — und der Effekt darf
    // das Formular nicht mit in den Fehler reissen. Ohne Animation, damit
    // Verzeichnis und Inhalt im selben Frame stehen statt zweier konkurrierender
    // Bewegungen (und damit der Sprung nicht abgebrochen werden kann).
    if (Math.abs(delta) > 4 && typeof root.scrollTo === "function") {
      root.scrollTo({ top: root.scrollTop + delta, behavior: "auto" });
    }
  }, [aktiverKey, currentSectionIdx, currentSubStepIdx]);

  return (
    <nav
      aria-label={ariaLabel}
      className={styles.root}
      ref={rootRef}
      style={maxHeight != null ? { maxHeight } : undefined}
    >
      <div className={styles.header} ref={headerRef}>
        <div className={styles.headerText}>
          <div className={styles.headerEyebrow}>{eyebrow}</div>
          <div className={styles.headerTitle}>{heading}</div>
        </div>
        {typeof progressFraction === "number" && (
          <div className={styles.progressBlock}>
            <div className={styles.progressMeta}>
              <span className={styles.progressTitle}>Fortschritt</span>
              {progressDetail && (
                <span className={styles.progressDetail}>{progressDetail}</span>
              )}
            </div>
            <CircularProgress
              value={progressFraction}
              dark
              ariaLabel={`Gesamtfortschritt ${Math.round(progressFraction * 100)}%`}
            />
          </div>
        )}
      </div>
      {hatScope && scopeSwitcher && (
        <div className={styles.scope} ref={scopeRef}>
          <div className={styles.scopeLabel}>{scopeSwitcher.label}</div>
          <div className={styles.scopePills}>
            {scopeSwitcher.items.map((item) => {
              const aktiv = item.id === scopeSwitcher.activeId;
              return (
                <button
                  key={item.id}
                  type="button"
                  aria-pressed={aktiv}
                  onClick={() => scopeSwitcher.onChange(item.id)}
                  className={mergeClasses(styles.scopePill, aktiv && styles.scopePillActive)}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {currentSection && (
        <div
          className={mergeClasses(styles.pinned, hatScope && styles.pinnedMitScope)}
          ref={pinnedRef}
        >
          <div className={styles.pinnedLabel}>Aktueller Punkt</div>
          <div className={styles.pinnedTitle}>
            {currentSubStep ? (
              <>
                <span
                  className={neutral ? styles.pinnedAbschnittNeutral : undefined}
                  style={
                    neutral
                      ? undefined
                      : { color: sectionColorHex[currentSection.color] }
                  }
                >
                  {currentSection.title} ·{" "}
                </span>
                {currentSubStep.title}
              </>
            ) : (
              <span
                className={neutral ? styles.pinnedAbschnittNeutral : undefined}
                style={
                  neutral
                    ? undefined
                    : { color: sectionColorHex[currentSection.color] }
                }
              >
                {currentSection.title}
              </span>
            )}
          </div>
        </div>
      )}
      <ol
        className={styles.list}
        style={bottomInset > 0 ? { paddingBottom: 32 + bottomInset } : undefined}
      >
        {sections.map((section, sIdx) => {
          const accent = sectionColorHex[section.color];
          const accentText = sectionColorHex[section.color];
          const isCurrentSection = sIdx === currentSectionIdx;
          // Der Abschnittskopf ist selbst ein Schritt; markiert wird er nur,
          // wenn kein Untereintrag aktiv ist.
          const isHeadActive = isCurrentSection && currentSubStepIdx < 0;
          const rowInhalt = (
            <>
              <span
                className={mergeClasses(
                  styles.sectionNumber,
                  isCurrentSection ? styles.sectionNumberActive : styles.sectionNumberInactive,
                  isCurrentSection && neutral && styles.sectionNumberActiveNeutral,
                )}
                style={!isCurrentSection && !neutral ? { color: accentText } : undefined}
              >
                {sIdx + 1}
              </span>
              <span className={styles.sectionTitle}>{section.title}</span>
            </>
          );
          const rowClass = mergeClasses(
            styles.sectionRow,
            onSectionSelect && styles.sectionRowBtn,
            isCurrentSection ? styles.sectionRowActive : styles.sectionRowInactive,
            isCurrentSection && neutral && styles.sectionRowActiveNeutral,
          );
          const rowStyle =
            isCurrentSection && !neutral ? { backgroundColor: accent } : undefined;
          return (
            <li key={section.key} className={styles.sectionItem}>
              {onSectionSelect ? (
                <button
                  ref={isHeadActive ? activeBtnRef : undefined}
                  type="button"
                  aria-current={isHeadActive ? "step" : undefined}
                  onClick={() => onSectionSelect(sIdx)}
                  className={rowClass}
                  style={rowStyle}
                >
                  {rowInhalt}
                </button>
              ) : (
                <div className={rowClass} style={rowStyle}>
                  {rowInhalt}
                </div>
              )}
              {section.subSteps.length > 0 && (
              <ul className={styles.subList}>
                {section.subSteps.map((sub, ssIdx) => {
                  const isActive = isCurrentSection && ssIdx === currentSubStepIdx;
                  const isPast = isCurrentSection && ssIdx < currentSubStepIdx;
                  return (
                    <li key={sub.key}>
                      <button
                        ref={isActive ? activeBtnRef : undefined}
                        type="button"
                        // Zweite, farbunabhängige Markierung des aktiven
                        // Eintrags — für Screenreader wie für die Optik.
                        aria-current={isActive ? "step" : undefined}
                        onClick={() => onSelect(sIdx, ssIdx)}
                        className={mergeClasses(
                          styles.subBtn,
                          isPast && styles.subBtnPast,
                          isActive && styles.subBtnActive,
                          isActive && neutral && styles.subBtnActiveNeutral,
                        )}
                        style={isActive && !neutral ? { color: accentText } : undefined}
                      >
                        {isActive ? "▸ " : ""}
                        {sub.title}
                      </button>
                    </li>
                  );
                })}
              </ul>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Sidebar;
